import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Plus, Edit, Trash2, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

interface Flower {
  id: string;
  name: string;
  color: string;
  type: string | null;
  stem_length_range: string | null;
  duration_range: string | null;
  image_url: string | null;
  image_path: string | null;
  created_at: string;
  updated_at: string;
}

export const FlowerCollection = () => {
  const [flowers, setFlowers] = useState<Flower[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingFlower, setEditingFlower] = useState<Flower | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: "",
    color: "",
    type: "",
    stem_length_range: "",
    duration_range: "",
    image_url: "",
  });

  useEffect(() => {
    fetchFlowers();
  }, []);

  const fetchFlowers = async () => {
    try {
      const { data, error } = await supabase
        .from("flowers")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setFlowers(data || []);
    } catch (error) {
      console.error("Error fetching flowers:", error);
      toast({
        title: "Error",
        description: "Failed to fetch flowers",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const flowerData = {
        name: formData.name,
        color: formData.color,
        type: formData.type || null,
        stem_length_range: formData.stem_length_range || null,
        duration_range: formData.duration_range || null,
        image_url: formData.image_url || null,
      };

      if (editingFlower) {
        const { error } = await supabase
          .from("flowers")
          .update(flowerData)
          .eq("id", editingFlower.id);

        if (error) throw error;
        toast({ title: "Success", description: "Flower updated successfully" });
      } else {
        const { error } = await supabase
          .from("flowers")
          .insert([flowerData]);

        if (error) throw error;
        toast({ title: "Success", description: "Flower added successfully" });
      }

      resetForm();
      fetchFlowers();
      setIsDialogOpen(false);
    } catch (error) {
      console.error("Error saving flower:", error);
      toast({
        title: "Error",
        description: "Failed to save flower",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this flower?")) return;

    try {
      const { error } = await supabase
        .from("flowers")
        .delete()
        .eq("id", id);

      if (error) throw error;
      toast({ title: "Success", description: "Flower deleted successfully" });
      fetchFlowers();
    } catch (error) {
      console.error("Error deleting flower:", error);
      toast({
        title: "Error",
        description: "Failed to delete flower",
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      color: "",
      type: "",
      stem_length_range: "",
      duration_range: "",
      image_url: "",
    });
    setEditingFlower(null);
  };

  const openEditDialog = (flower: Flower) => {
    setEditingFlower(flower);
    setFormData({
      name: flower.name,
      color: flower.color,
      type: flower.type || "",
      stem_length_range: flower.stem_length_range || "",
      duration_range: flower.duration_range || "",
      image_url: flower.image_url || "",
    });
    setIsDialogOpen(true);
  };

  const filteredFlowers = flowers.filter(flower =>
    flower.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    flower.color.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search flowers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="h-4 w-4 mr-2" />
              Add Flower
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingFlower ? "Edit Flower" : "Add New Flower"}
              </DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Rose, Tulip, etc."
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="color">Color</Label>
                <Input
                  id="color"
                  value={formData.color}
                  onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                  placeholder="Red, White, Pink, etc."
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="type">Type</Label>
                <Input
                  id="type"
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                  placeholder="Rose, Tulip, Chrysanthemum, etc."
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="stem_length">Stem Length Range</Label>
                <Input
                  id="stem_length"
                  value={formData.stem_length_range}
                  onChange={(e) => setFormData(prev => ({ ...prev, stem_length_range: e.target.value }))}
                  placeholder="50 - 120 cm"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="duration">Duration Range</Label>
                <Input
                  id="duration"
                  value={formData.duration_range}
                  onChange={(e) => setFormData(prev => ({ ...prev, duration_range: e.target.value }))}
                  placeholder="10 - 16 days"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="image_url">Image URL</Label>
                <Input
                  id="image_url"
                  value={formData.image_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                  placeholder="https://example.com/image.jpg"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave}>
                {editingFlower ? "Update" : "Add"} Flower
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-8">Loading flowers...</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredFlowers.map((flower) => (
            <Card key={flower.id}>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{flower.name}</CardTitle>
                    <Badge variant="secondary" className="mt-1">
                      {flower.color}
                    </Badge>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(flower)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(flower.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {flower.image_url && (
                  <img
                    src={flower.image_url}
                    alt={flower.name}
                    className="w-full h-32 object-cover rounded mb-3"
                  />
                )}
                <div className="space-y-1 text-sm text-muted-foreground">
                  {flower.stem_length_range && (
                    <div>Stem: {flower.stem_length_range}</div>
                  )}
                  {flower.duration_range && (
                    <div>Duration: {flower.duration_range}</div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {filteredFlowers.length === 0 && !loading && (
        <div className="text-center py-8 text-muted-foreground">
          {searchTerm ? "No flowers found matching your search." : "No flowers added yet."}
        </div>
      )}
    </div>
  );
};